/**
 * Watched items — commitments in a `waiting_*` state, with their recheck
 * condition (ticket body, plan §8).
 *
 * A commitment Yevhen marked "Waiting on…" must show
 * HERE, not disappear — `waiting_on` overrides a commitment's own `state`
 * and sets its recheck (plan §2.3), so the decision is the primary source
 * when present; the commitment's server-derived `state` is the fallback for
 * a row nobody has acted on yet. Side and recheck likewise prefer the
 * decision's own `waitingSide`/`recheckOn` over the roster-derived state.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type { HelmActionInput, HelmCommitment } from '@a7y/platform-health/helm';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';
import { RowName } from '../RowName';

export function Watched({
  commitments,
  onAction,
}: {
  commitments: HelmCommitment[];
  onAction: (input: HelmActionInput) => void;
}) {
  const watched = commitments.filter(
    (c) =>
      c.decision?.kind === 'waiting_on' ||
      (c.decision == null && (c.state === 'waiting_external' || c.state === 'waiting_internal'))
  );

  return (
    <HelmSection title="Watched" lineOn="none">
      {watched.length === 0 ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {watched.map((c) => {
              const side =
                c.decision?.waitingSide ??
                (c.state === 'waiting_external' ? 'external' : 'internal');
              const recheck = c.decision?.recheckOn ?? c.recheck;
              return (
                <TableRow key={c.key}>
                  <TableCell className="min-w-0 break-words font-medium">
                    <RowName commitment={c} />
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">
                    {side === 'external' ? 'waiting on them' : 'waiting on us'}
                    {c.decision?.waitingOn ? ` · ${c.decision.waitingOn}` : ''}
                    {recheck ? ` · recheck ${recheck}` : ''}
                  </TableCell>
                  <TableCell>
                    <ItemActionsMenu itemKey={c.key} decision={c.decision} onAction={onAction} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}
